import React from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { useConfiguration } from '../../../context/configurationContext';
import { EmptyState, NamedLink } from '../../../components';

import SaveSearchButton from '../SaveSearchButton/SaveSearchButton';

import css from './NoSearchResultsMaybe.module.css';

// URL params that are not user-facing filters and should never be offered as
// something to "remove", nor saved as part of a watched search.
//
// regNumber, carMake and carModel say which car the search is about. They are
// not conditions — the Marketplace API never sees them — so offering "remove
// the car" here would promise more results and deliver exactly the same ones.
// They are removable where they do belong: the "Din bil" chip above the list.
const NON_FILTER_PARAMS = [
  'page',
  'sort',
  'mapSearch',
  'bounds',
  'origin',
  'address',
  'searchType',
  'stock',
  'authorId',
  'showByUser',
  'listingType',
  'axle',
  // "Visa delar som passar" (P25009-482) — undone in the Din bil box and the
  // line above the results, never as a filter chip.
  'passar',
  'regNumber',
  'carMake',
  'carModel',
  'pub_isDeleted',
  'pub_listingType',
];

// Friendly labels for non-listing-field params.
const STATIC_LABELS = {
  price: 'Pris',
  keywords: 'Sökord',
  dates: 'Datum',
  pub_categoryLevel1: 'Bilmärke',
  pub_categoryLevel2: 'Bilmodell',
  /* Not a Console listing field, so it has no label of its own — and it is
   * the single strictest condition a car search can carry, so it must read
   * as something a buyer can recognise and drop. */
  pub_combination: 'Komplett sats (fram + bak)',
  /* "Inom 50 km" (P25009-425). */
  radius: 'Avstånd',
};

/* Place filters: dropping one reads as "show all of Sweden". */
const PLACE_FILTER_KEYS = ['pub_county', 'radius'];

const NoSearchResultsMaybe = props => {
  const { listingsAreLoaded, totalItems, location, resetAll } = props;
  const intl = useIntl();
  const config = useConfiguration();
  const history = useHistory();

  const hasNoResult = listingsAreLoaded && totalItems === 0;
  const hasSearchParams = location.search?.length > 0;

  /* P25009-432 BR-8 — per-filter result counts for the widening chips,
   * filled by the duck after a 0-result search. Empty until (and unless)
   * those count queries resolve; the chips render without counts then. */
  const wideningCounts = useSelector(state => state.SearchPage?.wideningCounts || {});
  const draftId = '00000000-0000-0000-0000-000000000000';
  const draftSlug = 'draft';

  const listingFields = config?.listing?.listingFields || [];

  // Resolve a human label for a URL filter param.
  const labelForParam = key => {
    if (STATIC_LABELS[key]) return STATIC_LABELS[key];
    if (key.startsWith('pub_')) {
      const fieldKey = key.slice(4);
      const field = listingFields.find(f => f.key === fieldKey);
      return field?.filterConfig?.label || field?.label || fieldKey;
    }
    return key;
  };

  // Active, removable filters from the current URL.
  const searchParams = new URLSearchParams(location.search || '');
  const removableFilters = [];
  searchParams.forEach((value, key) => {
    if (!NON_FILTER_PARAMS.includes(key) && value) {
      removableFilters.push({ key, label: labelForParam(key) });
    }
  });

  // Navigate to the same search with one filter removed (broadens the result).
  const removeFilter = key => {
    const next = new URLSearchParams(location.search || '');
    next.delete(key);
    next.delete('page');
    const search = next.toString();
    history.push({ pathname: location.pathname, search: search ? `?${search}` : '' });
  };

  if (!hasNoResult) return null;

  return (
    <div className={css.wrap}>
      <EmptyState
        title={intl.formatMessage({ id: 'SearchPage.noResults' })}
        lead={
          hasSearchParams
            ? 'Inga annonser matchar alla dina filter just nu. Ta bort ett filter för att bredda sökningen — eller bevaka sökningen så hör vi av oss när något dyker upp.'
            : 'Det finns inga annonser att visa just nu. Skapa en förfrågan så kontaktar säljare dig direkt.'
        }
        action={
          hasSearchParams
            ? {
                label: intl.formatMessage({ id: 'SearchPage.resetAllFilters' }),
                onClick: resetAll,
                variant: 'ghost',
              }
            : null
        }
      >
        {removableFilters.length > 0 ? (
          <div className={css.suggestions}>
            <p className={css.suggestionsTitle}>Bredda sökningen genom att ta bort ett filter:</p>
            <ul className={css.chips}>
              {removableFilters.map(filter => {
                /* BR-8 — each chip names how many results it unlocks; a
                 * chip that would still return nothing is not offered
                 * (BR-6). The place filter reads as the one-tap
                 * "Visa träffar i hela Sverige — N". */
                const count = wideningCounts[filter.key];
                if (count === 0) return null;
                const countLabel = count != null ? ` — ${count} träffar` : '';
                const chipLabel =
                  PLACE_FILTER_KEYS.includes(filter.key)
                    ? `Visa träffar i hela Sverige${count != null ? ` — ${count}` : ''}`
                    : `Ta bort ${filter.label}${countLabel}`;
                return (
                  <li key={filter.key}>
                    <button
                      type="button"
                      className={css.chip}
                      onClick={() => removeFilter(filter.key)}
                    >
                      <span className={css.chipX} aria-hidden="true">
                        ×
                      </span>
                      {chipLabel}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        {hasSearchParams ? (
          <div className={css.watchRow}>
            <SaveSearchButton className={css.watchButton} />
          </div>
        ) : null}

        <NamedLink
          className={css.createListingLink}
          name="EditListingPage"
          params={{ slug: draftSlug, id: draftId, type: 'new', tab: 'details' }}
          to={{ search: `listingType=buying` }}
        >
          <FormattedMessage id="SearchPage.createRequest" />
        </NamedLink>
      </EmptyState>
    </div>
  );
};

export default NoSearchResultsMaybe;
